import Vue from 'vue';
import Vuex from 'vuex';
import axios from 'axios';
import VuexPersist from 'vuex-persist';

const apiUrl = 'https://api.closetcloud.org';
const vuexPersist = new VuexPersist({
  key: 'sharebot',
  storage: localStorage,
  reducer: (state) => ({
    loginToken: state.loginToken,
  }),
});

Vue.use(Vuex);

export default new Vuex.Store({
  state: {
    loginToken: null,
    status: {},
    isPaused: false,
    updateInterval: null,
    offerStatus: {},
    jobs: {},
    sales: {},
  },
  getters: {
    isLoggedIn: (state) => state.loginToken != null,
    billingPortalLink: (state) => `${apiUrl}/billingPortalLink?auth_token=${state.loginToken}`,
  },
  actions: {
    startUpdater({ commit, dispatch }) {
      dispatch('fetchStatus');
      dispatch('stopUpdater');
      const interval = setInterval(() => {
        console.log('update run');
        dispatch('fetchStatus');
      }, 5 * 1000);
      commit('setUpdater', interval);
    },
    stopUpdater({ state, commit }) {
      clearInterval(state.updateInterval);
      commit('setUpdater', null);
    },
    getLoginToken(_, email) {
      return axios.post(`${apiUrl}/login`, {
        email,
      });
    },
    updateLoginToken({ commit }, loginToken) {
      commit('setLoginToken', loginToken);
    },
    signup(_, form) {
      return axios.post(`${apiUrl}/signup`, form);
    },
    fetchStatus({ state, commit }) {
      console.log('fetchStatus');
      if (!state.loginToken) {
        return false;
      }
      return axios.get(`${apiUrl}/status`, {
        headers: {
          Authorization: `Bearer ${state.loginToken}`,
        },
      })
        .then((response) => {
          if (response.statusCode === 401) {
            commit('setLoginToken', null);
          }
          commit('setStatus', response.data);
        });
    },
    async fetchUser({ state }) {
      const response = await axios.get(`${apiUrl}/user`, {
        headers: {
          Authorization: `Bearer ${state.loginToken}`,
        },
      });
      return response.data;
    },
    async fetchBrands({ state }) {
      const response = await axios.get(`${apiUrl}/brands`, {
        headers: {
          Authorization: `Bearer ${state.loginToken}`,
        },
      });
      return response.data.brands;
    },
    async fetchCatalog() {
      const response = await axios.get(`${apiUrl}/catalog`);
      return response.data;
    },
    togglePaused({ state, commit }) {
      if (!state.loginToken) {
        return false;
      }
      const method = state.isPaused ? 'resume' : 'pause';
      return axios({
        method: 'POST',
        url: `${apiUrl}/${method}`,
        headers: {
          Authorization: `Bearer ${state.loginToken}`,
        },
      })
        .then(() => {
          commit('setPaused', !state.isPaused);
        });
    },
    restart({ state, commit }) {
      if (!state.loginToken) {
        return false;
      }
      return axios({
        method: 'POST',
        url: `${apiUrl}/restart`,
        headers: {
          Authorization: `Bearer ${state.loginToken}`,
        },
      })
        .then(() => {
          commit('setPaused', false);
        });
    },
    async shareQuery({ state }, request) {
      if (!state.loginToken) {
        return false;
      }
      console.log(request);
      return axios.post(`${apiUrl}/shareQuery`,
        request,
        {
          headers: {
            Authorization: `Bearer ${state.loginToken}`,
          },
        });
    },
    async makeOffers({ state, dispatch, commit }, request) {
      if (!state.loginToken) {
        return false;
      }
      console.log(request);
      const response = await axios.post(`${apiUrl}/makeOffers`,
        request,
        {
          headers: {
            Authorization: `Bearer ${state.loginToken}`,
          },
        });
      const interval = setInterval(() => {
        console.log('update offer status');
        dispatch('fetchOfferStatus');
      }, 1000);
      console.log(response.data);
      commit('setOfferStatus', { jobId: response.data.jobId, interval });
      return response.data;
    },
    async fetchOfferListingsDetails({ state, commit }, filters) {
      if (state.offerStatus.status !== 'active') {
        commit('setOfferStatus', {});
      }
      const response = await axios.post(`${apiUrl}/listings`,
        filters,
        {
          headers: {
            Authorization: `Bearer ${state.loginToken}`,
          },
        });
      console.log(response.data);
      return response.data;
    },
    async fetchListings({ state }, filters) {
      const response = await axios.post(`${apiUrl}/listings`,
        filters,
        {
          headers: {
            Authorization: `Bearer ${state.loginToken}`,
          },
        });
      console.log(response.data);
      return response.data;
    },
    async fetchOfferStatus({ state, commit }) {
      const response = await axios.post(`${apiUrl}/jobStatus`,
        { jobId: state.offerStatus.jobId },
        {
          headers: {
            Authorization: `Bearer ${state.loginToken}`,
          },
        });
      commit('setOfferStatus', { status: response.data.status, progress: response.data.progress });
      console.log(response.data);
      if (response.data.status === 'completed') {
        clearInterval(state.offerStatus.interval);
      }
      return response.data;
    },
    async getSaleLabels({ state, dispatch, commit }) {
      if (!state.loginToken) {
        return false;
      }
      const response = await axios.post(`${apiUrl}/collateSaleLabels`,
        null,
        {
          headers: {
            Authorization: `Bearer ${state.loginToken}`,
          },
        });
      const { jobId } = response.data;
      const interval = setInterval(() => {
        console.log('update job status');
        dispatch('fetchJobStatus', jobId);
      }, 1000);
      console.log(response.data);
      commit('setJobStatus', { jobId, interval });
      return jobId;
    },
    async fetchJobStatus({ state, commit }, jobId) {
      const response = await axios.post(`${apiUrl}/jobStatus`,
        { jobId },
        {
          headers: {
            Authorization: `Bearer ${state.loginToken}`,
          },
        });
      commit('setJobStatus', { jobId, status: response.data });
      console.log(response.data);
      if (response.data.status === 'completed' || response.data.status === 'failed') {
        clearInterval(state.jobs[jobId].interval);
      }
      return response.data;
    },
    async fetchSalesSummary({ state, commit }) {
      const response = await axios.post(`${apiUrl}/sales`,
        { },
        {
          headers: {
            Authorization: `Bearer ${state.loginToken}`,
          },
        });
      console.log(response.data);
      commit('setSales', response.data);
      return response.data;
    },
  },
  mutations: {
    setUpdater(state, updater) {
      state.updateInterval = updater;
    },
    setOfferStatus(state, status) {
      const entries = Object.entries(status);
      if (entries.length === 0) {
        state.offerStatus = {};
        return;
      }
      entries.forEach(([key, value]) => {
        Vue.set(state.offerStatus, key, value);
      });
    },
    setJobStatus(state, { jobId, interval = null, status = null }) {
      if (!(jobId in state.jobs)) {
        Vue.set(state.jobs, jobId, {});
      }
      if (interval) {
        Vue.set(state.jobs[jobId], 'interval', interval);
      }
      if (status) {
        Vue.set(state.jobs[jobId], 'status', status);
      }
    },
    setLoginToken(state, loginToken) {
      state.loginToken = loginToken;
    },
    setSales(state, sales) {
      state.sales = sales;
    },
    setStatus(state, status) {
      state.status = status;
    },
    setPaused(state, isPaused) {
      state.isPaused = isPaused;
    },
  },
  plugins: [vuexPersist.plugin],
});
